// src/components/profiles/Member/components/posts/PostCarousel.tsx

"use client";

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import {
  Waypoints,
  BringToFront,
  AudioWaveform,
  HeartHandshake,
} from "lucide-react";

import MomentMyGrid from "./moment/MomentMyGrid";
import PrayerMyGrid from "./pray/PrayerMyGrid";
import JourneyPost from "./journey/JourneyPost";
import EchoLIT from "./echo-lit/EchoLITPost";
import "./PostCarousel.css";

// URL tab keys
type PostTabKey = "moments" | "prayers" | "journeys" | "echo";

const TAB_TO_INDEX: Record<PostTabKey, number> = {
  moments: 0,
  prayers: 1,
  journeys: 2,
  echo: 3,
};

const INDEX_TO_TAB: Record<number, PostTabKey> = {
  0: "moments",
  1: "prayers",
  2: "journeys",
  3: "echo",
};

const PostCarousel = () => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const slides = useMemo(
    () => [
      {
        key: "moments" as const,
        component: <MomentMyGrid />,
        icon: <BringToFront size={24} />,
        label: "Moment",
      },
      {
        key: "prayers" as const,
        component: <PrayerMyGrid />,
        icon: <HeartHandshake size={24} />,
        label: "Prayer",
      },
      {
        key: "journeys" as const,
        component: <JourneyPost />,
        icon: <Waypoints size={24} />,
        label: "Journey",
      },
      {
        key: "echo" as const,
        component: <EchoLIT />,
        icon: <AudioWaveform size={24} />,
        label: "EchoLIT",
      },
    ],
    []
  );

  // Read tab from URL (and force prayers tab if focusPrayer exists)
  const tabFromUrl = useMemo((): PostTabKey => {
    const raw = (searchParams?.get("postTab") || "").toLowerCase() as PostTabKey;
    const focusPrayer = searchParams?.get("focusPrayer");

    // If we are returning to a focused prayer, force Prayers tab
    if (focusPrayer) return "prayers";

    if (raw && raw in TAB_TO_INDEX) return raw;
    return "moments";
  }, [searchParams]);

  const [currentSlide, setCurrentSlide] = useState<number>(
    TAB_TO_INDEX[tabFromUrl] ?? 0
  );

  // Sync local state when URL changes (important after edit return)
  useEffect(() => {
    setCurrentSlide(TAB_TO_INDEX[tabFromUrl] ?? 0);
  }, [tabFromUrl]);

  const handleSlideChange = useCallback(
    (index: number) => {
      setCurrentSlide(index);

      // Keep URL in sync with active tab
      const params = new URLSearchParams(searchParams?.toString() || "");
      params.set("postTab", INDEX_TO_TAB[index] || "moments");

      // If user manually changes tab, remove prayer focus marker
      if (INDEX_TO_TAB[index] !== "prayers") {
        params.delete("focusPrayer");
      }

      const next = params.toString();
      router.replace(next ? `${pathname}?${next}` : pathname, { scroll: false });
    },
    [router, pathname, searchParams]
  );

  return (
    <div className="p-carousel-container">
      <div className="post-carousel-slides">
        {/* Indicators */}
        <div className="post-carousel-indicators tl-indicator-aurora">
          <div className="post-indicators-container">
            {slides.map((slide, index) => (
              <button
                key={slide.key}
                type="button"
                onClick={() => handleSlideChange(index)}
                className={`indicator ${currentSlide === index ? "active" : ""}`}
                aria-label={slide.label}
                title={slide.label}
              >
                {slide.label}
              </button>
            ))}
          </div>
        </div>

        {/* Slides */}
        <div
          className="p-carousel-inner"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {slides.map((slide) => (
            <div key={slide.key} className="carousel-slide">
              {slide.component}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PostCarousel;